import { useRef, useEffect } from "react";
import { Clock3, Loader2 } from "lucide-react";
import { useAppSelector } from "store";
import { selectAuthUser } from "store/slices/auth/auth.selector";
import { useContactOwners } from "service/crm";
import { ActivityGroup } from "types/crm/activity";
import ContactEmptyState from "../ContactEmptyState";
import ActivityItem from "./ActivityItem";
import Spinner from "components/Loaders/Spinner";
import { getGroupLabel } from "./activityConfig";
import OverdueSection from "./OverdueSection";
import { TaskQueryResult } from "../Tasks/TaskTab";

export interface ActivityQueryResult {
  groups: ActivityGroup[];
  isLoading: boolean;
  hasMore: boolean;
  isFetchingMore: boolean;
  onLoadMore: () => void;
}

interface AllActivityTabProps {
  isActive: boolean;
  entityUuid: string;
  useActivityQuery: (entityUuid: string) => ActivityQueryResult;
  /** The record's overdue tasks (task list API with `quickFilter=OVERDUE`). */
  useOverdueTasksQuery: (entityUuid: string) => TaskQueryResult;
  onOpenTask: (taskUuid: string) => void;
}

const AllActivityTab = ({
  isActive,
  entityUuid,
  useActivityQuery,
  useOverdueTasksQuery,
  onOpenTask,
}: AllActivityTabProps) => {
  const authUser = useAppSelector(selectAuthUser);
  const { data: owners = [] } = useContactOwners(authUser?.orgUuid as string);
  const { groups, isLoading, hasMore, isFetchingMore, onLoadMore } =
    useActivityQuery(entityUuid);
  const overdue = useOverdueTasksQuery(entityUuid);

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasMore) return;
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore]);

  if (!isActive) return null;
  if (isLoading || overdue.isLoading) return <Spinner />;

  const overdueSection = (
    <OverdueSection
      tasks={overdue.tasks}
      onOpenTask={onOpenTask}
      hasMore={overdue.hasMore}
      isFetchingMore={overdue.isFetchingMore}
      onLoadMore={overdue.onLoadMore}
    />
  );

  if (!groups.length) {
    return overdue.tasks.length ? (
      <div className="space-y-6">{overdueSection}</div>
    ) : (
      <ContactEmptyState
        icon={Clock3}
        title="No activity yet"
        description="Notes and tasks will show up here as soon as you add one."
      />
    );
  }

  return (
    <div className="space-y-6">
      {overdueSection}
      {groups.map((group) => (
  <div key={group.date}>
    <h4 className="text-xs font-medium text-neutral-faded mb-2 uppercase tracking-wide">
      {getGroupLabel(group)}
    </h4>
    <div className="space-y-3">
      {group.activities.map((activity) => (
        <ActivityItem key={activity.uuid} activity={activity} owners={owners} />
      ))}
    </div>
  </div>
))}
      <div className="flex flex-col items-center gap-2 py-2">
        {isFetchingMore && (
          <Loader2 className="w-5 h-5 animate-spin text-neutral-medium" />
        )}
        {hasMore && <div ref={sentinelRef} className="h-1 w-full" />}
      </div>
    </div>
  );
};

export default AllActivityTab;